import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Network, Pencil } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import { pcmpLookupDepartmentsQueryOptions } from '../../api/pcmpLookups.api';
import { pcmpDivisionLabel } from '../../constants/pcmpQueue.constants';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import type { PcmpLookupDepartment } from '../../types/pcmpLookups.types';
import { PcmpDepartmentDivisionDialog } from './PcmpDepartmentDivisionDialog';

/** Which PCMP division each region department reports under; admins change it. */
export function PcmpDepartmentDivisionsTable() {
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpLookupDepartmentsQueryOptions(),
  );
  const { can } = usePcmpPermissions();
  const canEdit = can(PCMP_PERMISSION.ADMIN);
  const [editing, setEditing] = useState<PcmpLookupDepartment | null>(null);

  const columns = useMemo<ColumnDef<PcmpLookupDepartment>[]>(() => {
    const base: ColumnDef<PcmpLookupDepartment>[] = [
      {
        accessorKey: 'name',
        header: 'Department',
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        id: 'division',
        header: 'PCMP division',
        cell: ({ row }) =>
          row.original.divisionCode ? (
            pcmpDivisionLabel(row.original.divisionCode)
          ) : (
            <span className="text-muted-foreground">{pcmpDivisionLabel(null)}</span>
          ),
      },
    ];
    if (!canEdit) return base;
    return [
      ...base,
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setEditing(row.original)}
          >
            <Pencil className="size-3.5" />
            Edit
          </Button>
        ),
      },
    ];
  }, [canEdit]);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Division Mapping</h1>
        <p className="text-sm text-muted-foreground">
          The PCMP division each department&apos;s MOs are grouped under in queues and reports.
        </p>
      </div>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the departments"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          className="min-h-0 flex-1"
        />
      ) : (
        <DataTable
          data={data ?? []}
          columns={columns}
          isLoading={isLoading}
          emptyIcon={Network}
          emptyMessage="No departments"
          className="flex-1 min-h-0"
        />
      )}

      {editing ? (
        <PcmpDepartmentDivisionDialog department={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}
